import { useEffect, useMemo, useState } from "react";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

const COMPONENT_CLASS = "c-report-dynamic-table";

type SortDirection = "asc" | "desc";

interface SortState {
    column: string;
    direction: SortDirection;
}

export interface TableColumn<T> {
    key: string;
    header: string;
    sortable?: boolean;
    render?: (row: T) => React.ReactNode;
    sortAccessor?: (row: T) => any;
    width?: string | number;
    minWidth?: string | number;
    maxWidth?: string | number;
    boldContentText?: boolean;
    fontWeight?: number;
}

interface GenericTableProps<T> {
    rows: T[];
    columns: TableColumn<T>[];
    pageSize?: number;
}

function ReportDynamicTable<T>({
    rows,
    columns,
    pageSize = 25,
}: GenericTableProps<T>) {

    const [sortState, setSortState] = useState<SortState>({
        column: columns[0]?.key ?? "",
        direction: "asc",
    });

    const [currentPage, setCurrentPage] = useState(1);

    /* Reset when rows change */
    useEffect(() => {
        setSortState({
            column: columns[0]?.key ?? "",
            direction: "asc",
        });
        setCurrentPage(1);
    }, [rows, columns]);

    /* Sorting */
    const sortedRows = useMemo(() => {
        if (!sortState.column) return rows;

        const column = columns.find(c => c.key === sortState.column);
        if (!column) return rows;

        return [...rows].sort((a, b) => {
            const valueA = column.sortAccessor
                ? column.sortAccessor(a)
                : (a as any)[column.key];

            const valueB = column.sortAccessor
                ? column.sortAccessor(b)
                : (b as any)[column.key];

            if (valueA == null) return 1;
            if (valueB == null) return -1;

            if (!isNaN(valueA) && !isNaN(valueB)) {
                return sortState.direction === "asc"
                    ? valueA - valueB
                    : valueB - valueA;
            }

            return sortState.direction === "asc"
                ? String(valueA).localeCompare(String(valueB))
                : String(valueB).localeCompare(String(valueA));
        });
    }, [rows, sortState, columns]);

    /* Pagination */
    const totalPages = Math.ceil(sortedRows.length / pageSize);

    const paginatedRows = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return sortedRows.slice(start, start + pageSize);
    }, [sortedRows, currentPage, pageSize]);

    const handleSort = (columnKey: string) => {
        setSortState(prev => {
            if (prev.column !== columnKey) {
                return { column: columnKey, direction: "asc" };
            }

            return {
                column: columnKey,
                direction: prev.direction === "asc" ? "desc" : "asc",
            };
        });
    };

    const getVisiblePages = () => {
        const pages: (number | "...")[] = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
            return pages;
        }

        const start = Math.max(2, currentPage - 2);
        const end = Math.min(totalPages - 1, currentPage + 2);

        pages.push(1);

        if (start > 2) pages.push("...");

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (end < totalPages - 1) pages.push("...");

        pages.push(totalPages);

        return pages;
    };

    return (
        <div className={COMPONENT_CLASS}>
            <div className={`${COMPONENT_CLASS}__scroll`}>

                <table style={{ minWidth: 1000, tableLayout: "fixed", borderSpacing: "0 8px", borderCollapse: "separate" }}>
                    <thead>
                        <tr className="bg-black-gray-1 p-x-2">
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    onClick={() =>
                                        col.sortable && handleSort(col.key)
                                    }
                                    style={{
                                        cursor: col.sortable ? "pointer" : "default",
                                        width: col.width,
                                        minWidth: col.minWidth,
                                        maxWidth: col.maxWidth,
                                        paddingTop: "0.5rem",
                                        paddingBottom: "0.5rem"
                                    }}
                                    className={`${COMPONENT_CLASS}__table-header label-large`}
                                >
                                    {col.header}
                                    {sortState.column === col.key &&
                                        (sortState.direction === "asc" ? " ▲" : " ▼")}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedRows.map((row, rowIndex) => (
                            <tr key={rowIndex} className={`${COMPONENT_CLASS}__table-row`}>
                                {columns.map(col => (
                                    <td key={col.key}
                                        style={{
                                            width: col.width,
                                            minWidth: col.minWidth,
                                            maxWidth: col.maxWidth,
                                            fontWeight: col.boldContentText ? "bold" : col.fontWeight ? col.fontWeight : 300,
                                        }}>
                                        {col.render
                                            ? col.render(row)
                                            : (row as any)[col.key]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className={`${COMPONENT_CLASS}__pagination`}>

                <button
                    className={`${COMPONENT_CLASS}__page-btn`}
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                >
                    <ChevronLeftIcon />
                </button>

                {getVisiblePages().map((page, i) => {

                    if (page === "...") {
                        return (
                            <span key={i} className={`${COMPONENT_CLASS}__ellipsis`}>
                                ...
                            </span>
                        );
                    }

                    return (
                        <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`${COMPONENT_CLASS}__page-btn ${page === currentPage
                                ? `${COMPONENT_CLASS}__page-btn--active`
                                : ""
                                }`}
                        >
                            {page}
                        </button>
                    );
                })}

                <button
                    className={`${COMPONENT_CLASS}__page-btn`}
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                >
                    <ChevronRightIcon />
                </button>

            </div>
        </div>
    );
}

export default ReportDynamicTable;